import React, { useState } from 'react';
import { Users, Calendar, FileText } from 'lucide-react';
import { Button } from '@/components/base';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { AdminStaffTab } from './AdminStaffTab';
import { AdminPatientsTab } from './AdminPatientsTab';
import { AdminAppointmentsTab } from './AdminAppointmentsTab';
import { AdminConsultationsTab } from './AdminConsultationsTab';
import type { OrganizationStatistics } from '@/lib/api/statistics';

type AdminTabKey = 'staff' | 'patients' | 'appointments' | 'consultations';

interface AdminStatisticsSectionProps {
  statistics?: OrganizationStatistics | null;
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  startDate?: Date;
  endDate?: Date;
  onStartDateChange?: (date: Date) => void;
  onEndDateChange?: (date: Date) => void;
}

export const AdminStatisticsSection: React.FC<AdminStatisticsSectionProps> = ({
  statistics,
  isLoading,
  error,
  onRefresh,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [activeTab, setActiveTab] = useState<AdminTabKey>('staff');

  const tabs: Array<{ key: AdminTabKey; label: string; icon: React.ReactNode }> = [
    { key: 'staff', label: 'Staff', icon: <Users className="h-4 w-4" /> },
    { key: 'patients', label: 'Patients', icon: <Users className="h-4 w-4" /> },
    { key: 'appointments', label: 'Appointments', icon: <Calendar className="h-4 w-4" /> },
    { key: 'consultations', label: 'Consultations', icon: <FileText className="h-4 w-4" /> },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'staff':
        return <AdminStaffTab statistics={statistics ?? null} isLoading={!!isLoading} />;
      case 'patients':
        return <AdminPatientsTab statistics={statistics ?? null} isLoading={!!isLoading} />;
      case 'appointments':
        return <AdminAppointmentsTab statistics={statistics ?? null} isLoading={!!isLoading} />;
      case 'consultations':
        return <AdminConsultationsTab statistics={statistics ?? null} isLoading={!!isLoading} />;
      default:
        return null;
    }
  };

  return (
    <div className="mb-4 flex w-full flex-shrink-0 flex-col gap-4 sm:mb-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-hanken text-xl font-medium leading-overview-title tracking-normal text-gray-700">
          Organization Statistics
        </h2>
        <div className="flex items-center gap-2">
          {startDate && endDate && onStartDateChange && onEndDateChange && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={onStartDateChange}
              onEndDateChange={onEndDateChange}
            />
          )}
          {onRefresh && (
            <Button onClick={onRefresh} variant="outline" size="sm">
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Admin Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 font-hanken text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'border-b-2 border-[#061A61] text-[#061A61]'
                : 'text-gray-600 hover:text-[#061A61]'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Admin Tab Content */}
      {error ? (
        <div className="py-4 text-center">
          <p className="font-hanken text-sm text-red-600">{error}</p>
        </div>
      ) : (
        renderTabContent()
      )}
    </div>
  );
};
